import { useCallback, MutableRefObject, Dispatch, SetStateAction } from 'react';

/**
 * Creates a callback function that accumulates paginated data across pages.
 * Replaces data on page 1, appends on subsequent pages.
 * Tracks accumulated pages to prevent duplicates when effects re-run.
 * Auto-increments itemsShown if user has seen all previous items.
 *
 * @param querySignatureRef - Ref to track query signature for filter changes
 * @param locationSearch - Current location.search string
 * @returns A memoized callback function for accumulating data
 */
export function useAccumulateData<T>(querySignatureRef: MutableRefObject<string>, locationSearch: string) {
  return useCallback(
    (
      data: T[] | undefined,
      page: number,
      itemsShown: number,
      setAccumulated: Dispatch<SetStateAction<T[]>>,
      setItemsShown: Dispatch<SetStateAction<number>>,
      initialItems: number,
      accumulatedPagesRef: MutableRefObject<Set<number>>,
    ) => {
      if (!data) return;

      if (page === 1) {
        setAccumulated(data);
        accumulatedPagesRef.current = new Set([1]);
        querySignatureRef.current = locationSearch;
      } else if (querySignatureRef.current === locationSearch && !accumulatedPagesRef.current.has(page)) {
        // Skip empty data for pages > 1 (likely loading state - wait for real data)
        if (data.length === 0) return;

        setAccumulated((prev) => {
          const newData = [...prev, ...data];
          // Auto-increment itemsShown if user has seen all previous items
          if (itemsShown >= prev.length) {
            setItemsShown((prevShown) => Math.min(prevShown + initialItems, newData.length));
          }
          return newData;
        });
        accumulatedPagesRef.current.add(page);
      }
    },
    [locationSearch, querySignatureRef],
  );
}
